// external dependencies
import React, { useState, useRef, useEffect } from 'react'
import { bool, number, objectOf, shape, string } from 'prop-types'
import styled from '@emotion/styled'
import { measure } from 'remeasure'
import { Link } from '@jelly/ui'

// components
import { Tab } from './Tab'
import { TabArrow } from './TabArrow'

// styled components
const TabWrap = styled.div`
	box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
	overflow-x: auto;
	white-space: nowrap;
	width: 100%;
	z-index: -1;

	::-webkit-scrollbar {
		display: none;
	}
`

const TabsContainer = styled.div`
	display: flex;
	margin-left: 32px;
	margin-right: 32px;
	padding-top: 5px;
	transition: left 0.15s ease;

	${({ theme }) => theme.MQ.M} {
		${({ displayArrow }) => (displayArrow ? `margin: 0 30px` : `margin: 0;`)};
		padding-bottom: 2px;
	}
`

const measureWithKeysAndOptions = measure(['clientWidth', 'scrollWidth'], { flatten: true })

export const Tabs = measureWithKeysAndOptions(
	({ areTabsDisabled = false, currentTab = '', tabChangeInterval = 70, tabData }) => {
		const tabKeys = Object.keys(tabData)
		const lastTab = tabKeys[tabData.length - 1]
		const [displayArrow, setDisplayArrow] = useState(null)
		const tabContainer = useRef(null)
		const clientWidth = tabContainer.current && tabContainer.current.clientWidth
		const scrollWidth = tabContainer.current && tabContainer.current.scrollWidth - 64 // account for arrow widths

		const handleResize = () => {
			const clientWidth = tabContainer.current.clientWidth
			const scrollWidth = tabContainer.current.scrollWidth - 64 // account for arrow widths
			setDisplayArrow(clientWidth < scrollWidth)
		}

		// display arrows based on scrollWidth vs clientWidth
		useEffect(() => {
			setDisplayArrow(clientWidth < scrollWidth)

			window.addEventListener('resize', handleResize)

			return () => {
				window.removeEventListener('resize', handleResize)
			}
		}, [tabContainer, clientWidth, scrollWidth])

		// click handler for right arrow
		const handleNextClick = (e) => {
			const tabsContainer = e.currentTarget.parentNode
			const newTabPosition = tabChangeInterval + tabsContainer.scrollLeft
			tabsContainer.scrollLeft = newTabPosition
		}

		// click handler for left arrow
		const handlePrevClick = (e) => {
			const tabsContainer = e.currentTarget.parentNode
			const newTabPosition = tabsContainer.scrollLeft - tabChangeInterval
			tabsContainer.scrollLeft = newTabPosition
		}

		return (
			<TabWrap ref={tabContainer}>
				<TabArrow direction="left" displayArrow={displayArrow} onClick={handlePrevClick} />
				<TabArrow direction="right" displayArrow={displayArrow} onClick={handleNextClick} />
				<TabsContainer displayArrow={displayArrow}>
					{tabKeys.map((key, index) => {
						const { label, url, rel } = tabData[key]
						return (
							<Link key={index} href={url} rel={rel}>
								<Tab
									displayArrow={displayArrow}
									isActive={currentTab === key}
									isLastTab={index === lastTab}
									label={label}
									areTabsDisabled={areTabsDisabled}
								/>
							</Link>
						)
					})}
				</TabsContainer>
			</TabWrap>
		)
	}
)

Tabs.propTypes = {
	areTabsDisabled: bool,
	clientWidth: number,
	currentTab: string,
	scrollWidth: number,
	tabChangeInterval: number,
	tabData: objectOf(
		shape({
			label: string.isRequired,
			url: string.isRequired,
		})
	).isRequired,
}
